import { AuthState, AuthType } from '../../../pollen/types';
import { decodeBase32, encodeBase32 } from '../../../util/base32';
import { decryptAukForPasskeyPRF } from '../../passkey';
import {
  type LockedPasskeyKeychain,
  type NoVaultKeyPasskeyKeychain,
  type OpenPasskeyKeychain,
  type VaultItem,
} from '../types';
import {
  acceptShare,
  encodeKeychain,
  getVaultItems,
  migrateMonolithicState,
  putVaultItem,
  shareItem,
} from '..';

import { baseState } from './base';
import type { ItemKeys, StateFunc, VaultEnv } from './types';

export function statePasskeyOpen(keychain: OpenPasskeyKeychain, itemKeys: ItemKeys): StateFunc {
  const { auk } = keychain;
  return ({ storage, vaultAPI, userAPI }) => ({
    ...baseState('passkeyOpen'),

    async putItem(item) {
      return putVaultItem(vaultAPI, itemKeys, auk, item);
    },

    async deleteItem(itemID) {
      await vaultAPI.deleteItem(itemID);
      delete itemKeys[itemID];
    },

    async items() {
      const [items, newItemKeys] = await getVaultItems(vaultAPI, auk, keychain.email);
      itemKeys = newItemKeys;
      return items;
    },

    async lock() {
      await userAPI.signOut();
      const newKeychain: LockedPasskeyKeychain = {
        state: 'passkeyLocked',
        auk: keychain.auk,
        email: keychain.email,
      };
      storage.setItem('clovyr/keychain', encodeKeychain(newKeychain));
      return statePasskeyLocked(newKeychain);
    },

    async registerPasskey(nickname) {
      return userAPI.registerPasskey({ nickname }, auk);
    },

    async shareItem(itemID, extraParams, policy, localSsoFqdn) {
      return shareItem(vaultAPI, auk, itemID, itemKeys, extraParams, policy, localSsoFqdn);
    },

    async acceptShare(itemID: string, shareSecret: string, localSsoToken?: string) {
      const [items, newItemKeys] = await acceptShare(
        vaultAPI,
        auk,
        itemID,
        shareSecret,
        keychain.email,
        localSsoToken
      );
      itemKeys = newItemKeys;
      return items;
    },

    secretKey() {
      return encodeBase32(keychain.auk);
    },

    authType() {
      return AuthType.Passkey;
    },

    authState() {
      return AuthState.LoggedIn;
    },

    email() {
      return keychain.email;
    },
  });
}

export function statePasskeyLocked(keychain: LockedPasskeyKeychain): StateFunc {
  return (env) => ({
    ...baseState('passkeyLocked'),

    async logInWithPasskey(email: string, recoveryKey?: string) {
      const loginEmail = email || keychain.email;
      return unlockPasskeyVault(env, loginEmail, recoveryKey, keychain.auk);
    },

    authType() {
      return AuthType.Passkey;
    },

    authState() {
      return AuthState.Locked;
    },

    email() {
      return keychain.email;
    },
  });
}

export function statePasskeyNoVaultKey(
  keychain: NoVaultKeyPasskeyKeychain,
  resetToAnonymousState: (env: VaultEnv) => StateFunc
): StateFunc {
  return (env) => ({
    ...baseState('passkeyNoVaultKey'),

    async addSecretKey(secretKey) {
      const auk = decodeBase32(secretKey);
      const { encryptedState } = await env.userAPI.readState(keychain.email);
      return openPasskeyVaultWithAUK(env, keychain.email, auk, encryptedState);
    },

    async lock() {
      await env.userAPI.signOut();
      return resetToAnonymousState(env);
    },

    authType() {
      return AuthType.Passkey;
    },

    authState() {
      return AuthState.LoggedInWithoutSecretKey;
    },

    email() {
      return keychain.email;
    },
  });
}

async function unlockPasskeyVault(
  env: VaultEnv,
  email: string,
  recoveryKey: string | undefined,
  cachedAUK?: Uint8Array
): Promise<[StateFunc, VaultItem[]]> {
  const passkeyResult = await env.userAPI.signInWithPasskey({ email });
  let auk = cachedAUK;
  if (passkeyResult.response.vaultEnvelope && passkeyResult.prfOutput) {
    auk = await decryptAukForPasskeyPRF(
      passkeyResult.response.vaultEnvelope,
      passkeyResult.prfOutput,
      passkeyResult.credentialID
    );
  } else if (!auk && recoveryKey) {
    auk = decodeBase32(recoveryKey);
  }
  if (!auk) {
    throw new Error('passkey vault is not enrolled on this device');
  }
  return openPasskeyVaultWithAUK(env, email, auk, passkeyResult.response.encryptedState);
}

async function openPasskeyVaultWithAUK(
  { storage, userAPI, vaultAPI, migrator }: VaultEnv,
  email: string,
  auk: Uint8Array,
  encryptedState?: string
): Promise<[StateFunc, VaultItem[]]> {
  let items: VaultItem[];
  let itemKeys: ItemKeys;
  if (encryptedState) {
    [items, itemKeys] = await migrateMonolithicState(
      encryptedState,
      auk,
      userAPI,
      vaultAPI,
      migrator
    );
  } else {
    [items, itemKeys] = await getVaultItems(vaultAPI, auk, email);
  }

  const newKeychain: OpenPasskeyKeychain = {
    state: 'passkeyOpen',
    auk,
    email,
  };
  storage.setItem('clovyr/keychain', encodeKeychain(newKeychain));

  return [statePasskeyOpen(newKeychain, itemKeys), items];
}
